import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useSearch } from "@tanstack/react-router"
import React, { useEffect, useMemo, useState } from "react"

import { tabLabelToAccountType } from "../../constants"
import PaymentMethodDialog from "../../dialogs/PaymentMethodDialog"
import { useAccountsPayableBalancePermissions } from "../../hooks/useAccountsPayableBalancePermissions"
import { useCompanyAccountTypes } from "../../hooks/useCompanyAccountTypes"
import type { PaymentAccountTabLabel } from "../../types"

import { useDefaultPaymentMethod } from "./../../hooks/useDefaultPaymentMethod"
import AddPaymentMethodAlert from "./AddPaymentMethodAlert"
import { HideInactiveMethodsSwitch } from "./HideInactiveMethodsSwitch"
import PaymentMethodsList from "./PaymentMethodsList"

import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { ConfirmationDialog } from "@/components/common/Modals"
import PageTabs from "@/components/common/PageTabs/PageTabs"
import { PermissionActionType } from "@/constants/permissions"
import { CompanyStatus } from "@/graphql/codegen/graphql"
import { useDialogState } from "@/hooks/useDialogState"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useCompanyStore } from "@/screens/Companies/stores"
import { DefaultPaymentMethodConfirmModal } from "@/screens/CompanySettings/Accounting/components/PaymentTypeDetails/PaymentTypeCard/actions/DefaultPaymentMethodConfirmModal"
import useCompanyPaymentTypes from "@/screens/CompanySettings/Accounting/hooks/useCompanyPaymentTypes"

interface PaymentTypeDetailsSectionProps {
  companyId: string
}

const AddPaymentMethodButton = ({ handleAddPaymentMethod }: { handleAddPaymentMethod: () => void }) => {
  return (
    <Button appearance='outlined' startIcon={<AddOutlined />} onClick={handleAddPaymentMethod}>
      Add Payment Type
    </Button>
  )
}

export const PaymentTypeDetailsSection: React.FC<PaymentTypeDetailsSectionProps> = ({ companyId }) => {
  const {
    paymentTypes: _paymentTypes,
    paymentMethods,
    loading,
    refetchPaymentTypes,
    deletePaymentType,
    deletePaymentTypeMethod,
    togglePaymentMethodActivation,
  } = useCompanyPaymentTypes({
    companyId,
  })
  const company = useCompanyStore((state) => state.company)
  const [hideInactiveMethods, setHideInactiveMethods] = useState(true)
  const { defaultChangeData, handleDefaultChange, handleConfirmDefaultChange, handleCancelDefaultChange } =
    useDefaultPaymentMethod({ companyId, onRefetch: refetchPaymentTypes })
  const { hasPermissions } = usePermissionManager()

  const { availableTabs } = useCompanyAccountTypes()
  const { canViewAccountsPayableBalance } = useAccountsPayableBalancePermissions({ companyId })
  const [methodToDeactivate, setMethodToDeactivate] = useState<{
    methodId: string
    currentStatus: CompanyStatus
  } | null>(null)

  const {
    isDialogOpen,
    openDialog: handleAddPaymentMethod,
    closeDialog: handleCloseDialog,
    handleSuccess,
  } = useDialogState({
    onSuccess: refetchPaymentTypes,
  })

  const searchParams = useSearch({ from: "/app/companies/$companyId/edit/_layout/accounting" })

  const paymentTypes = useMemo(() => {
    if (hideInactiveMethods) {
      return _paymentTypes
        .map((paymentType) => ({
          ...paymentType,
          paymentMethods: paymentType.paymentMethods?.filter(
            (paymentMethod) => paymentMethod?.status !== CompanyStatus.Inactive
          ),
        }))
        .filter((paymentType) => Boolean(paymentType.paymentMethods?.length))
    }
    return _paymentTypes
  }, [_paymentTypes, hideInactiveMethods])

  const toggleActivation = (methodId: string, currentStatus: CompanyStatus) => {
    togglePaymentMethodActivation(methodId, currentStatus)
    setMethodToDeactivate(null)
  }

  const handleToggleActivation = (methodId: string, currentStatus: CompanyStatus) => {
    if (currentStatus === CompanyStatus.Inactive) {
      toggleActivation(methodId, currentStatus)
    } else {
      setMethodToDeactivate({ methodId, currentStatus })
    }
  }

  const isLastPaymentMethod = useMemo(() => {
    const activeMethods = paymentMethods.filter((method) => method.status !== CompanyStatus.Inactive)
    return activeMethods.length === 1
  }, [paymentMethods])

  useEffect(() => {
    if (searchParams.companyPaymentTypeId && !isDialogOpen) {
      handleAddPaymentMethod()
    }
  }, [searchParams, handleAddPaymentMethod, isDialogOpen])

  if (!hasPermissions([PermissionActionType.ACCOUNTING_MAINTENANCE])) {
    return null
  }

  if (loading) {
    return <CircularLoadingIndicator />
  }

  return (
    <Box className='space-y-4'>
      {paymentMethods.length ? (
        <PageTabs<PaymentAccountTabLabel>
          tabs={availableTabs}
          // The Accounts Payable sub-tab already names a vendor-only company's single account type.
          hideTabs={canViewAccountsPayableBalance && availableTabs.length === 1}
          renderContent={(activeTab: PaymentAccountTabLabel) => {
            const accountType = tabLabelToAccountType.get(activeTab)!
            const filteredPaymentTypes = paymentTypes.filter((paymentType) => paymentType.accountType === accountType)

            return (
              <PaymentMethodsList
                paymentTypes={filteredPaymentTypes}
                onAddPaymentMethod={handleAddPaymentMethod}
                onDefaultChange={handleDefaultChange}
                onRefetch={refetchPaymentTypes}
                onDeleteType={deletePaymentType}
                onDeleteMethod={deletePaymentTypeMethod}
                onToggleActivation={handleToggleActivation}
              />
            )
          }}
          renderSideContent={() => (
            <Box className='flex gap-4'>
              <HideInactiveMethodsSwitch
                onChange={() => setHideInactiveMethods((prev) => !prev)}
                isActive={hideInactiveMethods}
              />

              <AddPaymentMethodButton handleAddPaymentMethod={handleAddPaymentMethod} />
            </Box>
          )}
        />
      ) : (
        <AddPaymentMethodAlert onAddPaymentMethod={handleAddPaymentMethod} />
      )}

      {isDialogOpen ? (
        <PaymentMethodDialog
          companyId={companyId}
          paymentTypes={paymentTypes}
          onClose={handleCloseDialog}
          onSuccess={handleSuccess}
        />
      ) : null}

      {defaultChangeData ? (
        <DefaultPaymentMethodConfirmModal
          newDefaultPaymentMethod={defaultChangeData.newDefault}
          currentDefaultPaymentMethod={defaultChangeData.currentDefault}
          onConfirm={() => handleConfirmDefaultChange(defaultChangeData)}
          onClose={handleCancelDefaultChange}
        />
      ) : null}

      {methodToDeactivate ? (
        <ConfirmationDialog
          title={
            isLastPaymentMethod
              ? `This will remove all active payment methods for ${company?.name}.`
              : "This will make this payment method unavailable for transactions."
          }
          content={
            isLastPaymentMethod
              ? "You will need to add new payment information or reactivate existing methods to process transactions. Do you wish to proceed?"
              : "Do you wish to proceed?"
          }
          closeLabel='Cancel'
          confirmLabel='Deactivate'
          onConfirm={() => toggleActivation(methodToDeactivate.methodId, methodToDeactivate.currentStatus)}
          onClose={() => setMethodToDeactivate(null)}
        />
      ) : null}
    </Box>
  )
}
